import { DateRange } from 'rewire-common';
import { GQL } from 'rewire-graphql';
import { ITimecardTest, ISubscriptionRecord, TimecardTestFilterInput } from '../DataTypes';
import DataQueries from '../graphql/Queries';
import gql from 'graphql-tag';
import PagingModel from '../settings/PagingModel';
import EntitySubscriptionService, { EntitySubscriptionModel, SubscriptionCallbackFn } from './EntitySubscriptionService';
import { subscribe } from '../graphql/GraphQLClient';

const timecardTestsSubscription = (returnSchema: string) => gql`
subscription($filter: TimecardTestFilterInput!) {
  tests(filter: $filter) {
    test {
      ${returnSchema}
    }
    operation
  }
}`;

export function subscribeToTimecardTests(returnSchema: string, filter: TimecardTestFilterInput, callback: any): any {
  return subscribe(timecardTestsSubscription(returnSchema) as GQL, { filter }, callback, 'tests');
}

export type TimecardTestSort = (t1: ITimecardTest, t2: ITimecardTest) => number;

export function defaultTimecardTestSort(t1: ITimecardTest, t2: ITimecardTest) {
  return t1.name.localeCompare(t2.name);
}

export const defaultTimecardTestSchema = `
  id
  name
  code
  employee {
    id
    name
    code
  }
  effective
  status
`;

export class TimecardTestsSubscriptionModel extends EntitySubscriptionModel<ITimecardTest> {
  constructor(options?: {
    returnSchema?: string;
    filter?: TimecardTestFilterInput;
    sort?: TimecardTestSort;
    pagingModel?: PagingModel;
    externalEntitiesContainer?: ITimecardTest[];
    subscriptionCallback?: SubscriptionCallbackFn<ITimecardTest>;
  }) {
    super({
      returnSchema: options?.returnSchema || defaultTimecardTestSchema,
      filter: options?.filter as Record<string, unknown> | undefined,
      sort: options?.sort || defaultTimecardTestSort,
      pagingModel: options?.pagingModel,
      externalEntitiesContainer: options?.externalEntitiesContainer,
      subscriptionCallback: options?.subscriptionCallback,
    });
  }

  set tests(value: ITimecardTest[]) {
    this.entities = value;
  }

  get tests(): ITimecardTest[] {
    return this.entities;
  }

  get testCount(): number {
    return this.entityCount;
  }

  protected async _getEntities(
    returnSchema: string,
    filter?: Record<string, unknown>,
    pagingModel?: PagingModel,
  ): Promise<ITimecardTest[]> {
    const result = await DataQueries.getTimecardTests(
      returnSchema,
      filter,
      undefined,
      pagingModel?.itemsPerPage ?? 200,
      pagingModel?.currentPage,
    );
    return (result?.data ?? []) as ITimecardTest[];
  }

  protected subscribe(returnSchema: string, filter: Record<string, unknown>, callback: any): any {
    return subscribeToTimecardTests(returnSchema, filter, callback);
  }

  protected mergeAccessor(testRecord: ISubscriptionRecord<ITimecardTest>): ITimecardTest {
    return testRecord['test'] as ITimecardTest;
  }

  protected async merge(testRecord: ISubscriptionRecord<ITimecardTest>) {
    const test = this.mergeAccessor(testRecord);
    test.effective = new DateRange((test.effective as any).value); // test sent by subscription has malformed DateRange object.
    await super.merge(testRecord);
  }
}

class TimecardTestsSubscriptionService extends EntitySubscriptionService<ITimecardTest, TimecardTestsSubscriptionModel> {
  constructor() {
    super();
  }

  protected _createSubscriptionModel(options?: {
    returnSchema?: string;
    filter?: TimecardTestFilterInput;
    sort?: TimecardTestSort;
    pagingModel?: PagingModel;
    externalEntitiesContainer?: ITimecardTest[];
    subscriptionCallback?: SubscriptionCallbackFn<ITimecardTest>;
  }): TimecardTestsSubscriptionModel {
    return new TimecardTestsSubscriptionModel(options);
  }
}

const timecardTestsSubscriptionService = new TimecardTestsSubscriptionService();
export default timecardTestsSubscriptionService;
